import React from 'react';
import {
  CalendarOutlined,
  ShoppingCartOutlined,
  ScheduleOutlined,
  MailOutlined,
  LikeOutlined,
  BarChartOutlined,
  SettingOutlined,
  ShopOutlined,
  CarryOutOutlined,
  ApartmentOutlined,
  TeamOutlined,
  BlockOutlined,
  UserOutlined,
  UnorderedListOutlined,
  NotificationOutlined,
  CommentOutlined,
  FieldTimeOutlined,
  DashboardOutlined,
  CreditCardOutlined,
  FundProjectionScreenOutlined,
  ToolOutlined,
  EnvironmentOutlined,
  LockOutlined,
  HomeOutlined,
  GlobalOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { Tag } from 'antd';
import { WSB_MERCHANT_SOURCE } from '../../../shared/constants/merchant';
import type { SessionPermissionsState } from '../../../reducers/sessionPermissions';

export interface MenuItem {
  id?: string;
  key: string;
  icon?: React.ReactNode;
  label: React.ReactNode;
  children?: MenuItem[];
  className?: string;
}

const GRAVITY_MIGRATED_APP_ROUTES: Record<string, string> = {
  reminders: '/reminders-v2',
};

interface GetGravityAwareKeyParams {
  id: string;
  defaultKey: string;
  showGravityBasedApps: boolean;
}

const getGravityAwareKey = ({
  id,
  defaultKey,
  showGravityBasedApps,
}: GetGravityAwareKeyParams): string => {
  if (!showGravityBasedApps) return defaultKey;
  return GRAVITY_MIGRATED_APP_ROUTES[id] ?? defaultKey;
};

export interface MaybeLockedMenuItem {
  id?: string;
  key: string;
  icon: React.ReactNode;
  label: string;
  translate: (key: string) => string;
  isLocked: boolean;
  isReseller?: boolean;
  isTrialUser?: boolean;
  isWhitelabel?: boolean;
}

const createMaybeLockedMenuItem = ({
  id,
  key,
  icon,
  label,
  translate,
  isLocked = false,
  isReseller = false,
  isTrialUser = false,
  isWhitelabel = false,
}: MaybeLockedMenuItem): MenuItem[] => {
  if (isTrialUser) return [];
  if (isLocked && (isWhitelabel || isReseller)) return [];
  return [
    {
      id,
      key,
      icon,
      className: isLocked ? 'menu-item-locked' : '',
      label: (
        <span>
          {translate(label)}
          {isLocked && (
            <LockOutlined
              style={{
                fontSize: 12,
                marginLeft: 4,
                cursor: 'pointer',
              }}
            />
          )}
        </span>
      ),
    },
  ];
};

const getDashboardMenuItem = (config: MenuConfiguration): MenuItem[] => {
  const { sessionPermissions, translate } = config;
  if (!sessionPermissions?.dashboard?.available) return [];
  return [
    {
      id: 'dashboard',
      key: '#/dashboard',
      icon: <HomeOutlined />,
      label: translate('side-menu.home'),
    },
  ];
};

const getCalendarMenuItem = (config: MenuConfiguration): MenuItem[] => {
  const { sessionPermissions, translate } = config;
  if (!sessionPermissions?.calendar?.available) return [];
  return [
    {
      id: 'calendar',
      key: '/calendar',
      icon: <CalendarOutlined />,
      label: translate('side-menu.calendar'),
    },
  ];
};

const getCalendarV2MenuItem = (config: MenuConfiguration): MenuItem[] => {
  const { sessionPermissions, showCalendarV2, translate } = config;
  if (!showCalendarV2) return [];
  if (!sessionPermissions?.calendar?.available) return [];
  return [
    {
      id: 'calendar_v2',
      key: '/calendar-v2',
      icon: <CalendarOutlined />,
      label: translate('side-menu.calendar-v2'),
    },
  ];
};

const getPOSManagerMenuItem = (config: MenuConfiguration): MenuItem[] => {
  const {
    isWhitelabel,
    isReseller,
    posManagerUrl,
    isUserAdmin,
    isPOSUpsellActive,
    isPOSExternalLinkActive,
    translate,
  } = config;

  return isUserAdmin && isPOSExternalLinkActive
    ? createMaybeLockedMenuItem({
        id: 'pos_manager',
        key: isPOSUpsellActive ? '/upsell/shorepos' : posManagerUrl,
        icon: <ShoppingCartOutlined />,
        label: 'side-menu.shore-pos',
        translate,
        isLocked: isPOSUpsellActive,
        isWhitelabel,
        isReseller,
      })
    : [];
};

const getBookingPageMenuItem = (config: MenuConfiguration): MenuItem[] => {
  const { merchantSlug, bookingPageUrl, translate } = config;
  if (!merchantSlug) return [];
  return [
    {
      id: 'booking_page',
      key: bookingPageUrl,
      icon: <ShopOutlined />,
      label: translate('side-menu.booking-page'),
    },
  ];
};

const getShiftPlanMenuItem = (config: MenuConfiguration): MenuItem[] => {
  const { isWhitelabel, isReseller, sessionPermissions, translate } = config;
  const isLocked = sessionPermissions?.shiftplan?.available === false;
  const key = sessionPermissions?.shiftplan?.available
    ? '/shiftplan'
    : '/upsell/shiftplan';

  return createMaybeLockedMenuItem({
    id: 'shift_plan',
    key,
    icon: <CarryOutOutlined />,
    label: 'side-menu.shift-plan',
    translate,
    isLocked,
    isWhitelabel,
    isReseller,
  });
};

const getAIReceptionistMenuItem = (config: MenuConfiguration): MenuItem[] => {
  const { showAIReceptionist, translate } = config;
  if (!showAIReceptionist) return [];
  return [
    {
      id: 'ai_receptionist',
      key: '/ai-receptionist',
      icon: <RobotOutlined />,
      label: (
        <span>
          {translate('side-menu.ai-receptionist')}
          <Tag style={{ marginInlineStart: 8 }}>
            {translate('side-menu.beta-badge')}
          </Tag>
        </span>
      ),
    },
  ];
};

const getManagementMenuItems = (config: MenuConfiguration): MenuItem[] => {
  let children: MenuItem[] = [];
  const { sessionPermissions, showGravityBasedApps, translate } = config;
  const isCustomersAppEnabled = sessionPermissions?.customers?.available;
  const isResourcesAppEnabled = sessionPermissions?.resources?.available;
  const isStaffAppEnabled = sessionPermissions?.hr?.available;
  const isServicesAppEnabled = sessionPermissions?.services?.available;

  if (isCustomersAppEnabled) {
    children.push({
      id: 'customers',
      key: '/customers',
      icon: <TeamOutlined />,
      label: translate('side-menu.customers'),
    });
  }
  if (isResourcesAppEnabled) {
    children.push({
      id: 'resources',
      key: '/resources',
      icon: <BlockOutlined />,
      label: translate('side-menu.resources'),
    });
  }
  if (isStaffAppEnabled) {
    children.push({
      id: 'employees',
      key: '/hr',
      icon: <UserOutlined />,
      label: translate('side-menu.employees'),
    });
  }
  if (isServicesAppEnabled) {
    children.push({
      id: 'services',
      key: '/services',
      icon: <UnorderedListOutlined />,
      label: translate('side-menu.services'),
    });
  }
  if (showGravityBasedApps && isServicesAppEnabled) {
    children.push({
      id: 'servicesv2',
      key: '/services-v2',
      icon: <UnorderedListOutlined />,
      label: translate('side-menu.servicesv2'),
    });
    children.push({
      id: 'categories',
      key: '/services-v2/service-categories',
      icon: <UnorderedListOutlined />,
      label: translate('side-menu.categories'),
    });
  }
  if (children.length === 0) return [];
  return [
    {
      id: 'management',
      key: 'management',
      icon: <ApartmentOutlined />,
      label: translate('side-menu.management'),
      children,
    },
  ];
};

const getMarketingMenuItems = (config: MenuConfiguration): MenuItem[] => {
  const {
    sessionPermissions,
    isWhitelabel,
    isReseller,
    showGravityBasedApps,
    translate,
  } = config;

  const isMessengerAppEnabled = sessionPermissions?.messenger?.available;
  const isNewsletterAppEnabled = sessionPermissions?.newsletter?.available;
  const isFeedbackAppEnabled = sessionPermissions?.feedback?.available;
  const isRemindersAppEnabled = sessionPermissions?.reminders?.available;

  const messengerMenuItem = createMaybeLockedMenuItem({
    id: 'messenger',
    key: isMessengerAppEnabled ? '#/messenger' : '/upsell/messenger',
    icon: <CommentOutlined />,
    label: 'side-menu.messenger',
    translate,
    isLocked: !isMessengerAppEnabled,
    isReseller,
    isWhitelabel,
  });

  const newsletterMenuItem = createMaybeLockedMenuItem({
    id: 'newsletter',
    key: isNewsletterAppEnabled ? '/newsletter' : '/upsell/newsletter',
    icon: <MailOutlined />,
    label: 'side-menu.newsletter',
    translate,
    isLocked: !isNewsletterAppEnabled,
    isReseller,
    isWhitelabel,
  });

  const feedbackMenuItem = isFeedbackAppEnabled
    ? [
        {
          id: 'feedback',
          key: '/feedback',
          icon: <LikeOutlined />,
          label: translate('side-menu.feedback'),
        },
      ]
    : [];

  const remindersMenuItem = isRemindersAppEnabled
    ? [
        {
          id: 'reminders',
          key: getGravityAwareKey({
            id: 'reminders',
            defaultKey: '/reminders',
            showGravityBasedApps,
          }),
          icon: <FieldTimeOutlined />,
          label: translate('side-menu.reminders'),
        },
      ]
    : [];

  const children: MenuItem[] = [
    ...messengerMenuItem,
    ...newsletterMenuItem,
    ...feedbackMenuItem,
    ...remindersMenuItem,
  ].filter(Boolean);

  if (children.length === 0) return [];
  return [
    {
      id: 'marketing',
      key: 'marketing',
      icon: <NotificationOutlined />,
      label: translate('side-menu.marketing'),
      children,
    },
  ];
};

const getAnalysisMenuItems = (config: MenuConfiguration): MenuItem[] => {
  const {
    sessionPermissions,
    isTrialUser,
    isWhitelabel,
    isReseller,
    translate,
  } = config;

  const isInsightsAppEnabled = sessionPermissions?.insights?.available;
  const isTransactionsAppEnabled = sessionPermissions?.transactions?.available;
  const isReportingsAppEnabled = sessionPermissions?.reportings?.available;

  const insightsMenuItem: MenuItem[] = isInsightsAppEnabled
    ? [
        {
          id: 'insights',
          key: '/insights',
          icon: <DashboardOutlined />,
          label: translate('side-menu.insights'),
        },
      ]
    : [];

  const transactionsMenuItem: MenuItem[] = isTransactionsAppEnabled
    ? [
        {
          id: 'transactions',
          key: '/transactions',
          icon: <CreditCardOutlined />,
          label: translate('side-menu.transactions'),
        },
      ]
    : [];

  const posReportingsMenuItem = createMaybeLockedMenuItem({
    key: isReportingsAppEnabled ? '/pos-reports-v2' : '/upsell/reports',
    icon: <FundProjectionScreenOutlined />,
    label: 'side-menu.reports',
    translate,
    isLocked: !isReportingsAppEnabled,
    isReseller,
    isTrialUser,
    isWhitelabel,
  });

  const children: MenuItem[] = [
    ...insightsMenuItem,
    ...transactionsMenuItem,
    ...posReportingsMenuItem,
  ].filter(Boolean);
  if (children.length === 0) return [];

  return [
    {
      id: 'analysis',
      key: 'analysis',
      icon: <BarChartOutlined />,
      label: translate('side-menu.analysis'),
      children,
    },
  ];
};

const getSetupMenuItems = (config: MenuConfiguration): MenuItem[] => {
  const { sessionPermissions, hasMultipleLocations, source, translate } =
    config;
  const isSettingsAppEnabled = sessionPermissions?.settings?.available;

  const calendarSettingsMenuItem = isSettingsAppEnabled
    ? [
        {
          id: 'calendar_settings',
          key: '/application-settings',
          icon: <ScheduleOutlined />,
          label: translate('side-menu.calendar-settings'),
        },
      ]
    : [];
  const companySettingsMenuItem = isSettingsAppEnabled
    ? [
        {
          id: 'company_settings',
          key: '/settings',
          icon: <ToolOutlined />,
          label: translate('side-menu.company-settings'),
        },
      ]
    : [];
  const locationSettingsMenuItem = hasMultipleLocations
    ? [
        {
          id: 'location_settings',
          key: '/locations',
          icon: <EnvironmentOutlined />,
          label: translate('side-menu.location-settings'),
        },
      ]
    : [];
  const userSettingsMenuItem =
    source === WSB_MERCHANT_SOURCE
      ? [
          {
            id: 'user_settings',
            key: '/user-profile',
            icon: <UserOutlined />,
            label: translate('side-menu.user-settings'),
          },
        ]
      : [];
  const networksMenuItem = sessionPermissions?.networks?.available
    ? [
        {
          id: 'networks',
          key: '/networks',
          icon: <GlobalOutlined />,
          label: translate('side-menu.networks'),
        },
      ]
    : [];

  const children: MenuItem[] = [
    ...userSettingsMenuItem,
    ...calendarSettingsMenuItem,
    ...companySettingsMenuItem,
    ...locationSettingsMenuItem,
    ...networksMenuItem,
  ].filter(Boolean);
  if (children.length === 0) return [];

  return [
    {
      id: 'setup',
      key: 'setup',
      icon: <SettingOutlined />,
      label: translate('side-menu.setup'),
      children,
    },
  ];
};

export interface MenuConfiguration {
  sessionPermissions: SessionPermissionsState;
  isTrialUser: boolean;
  isWhitelabel: boolean;
  isReseller: boolean;
  isUserAdmin: boolean;
  isPOSUpsellActive: boolean;
  isPOSExternalLinkActive: boolean;
  bookingPageUrl: string;
  posManagerUrl: string;
  merchantSlug?: string;
  currentAccountId?: string;
  hasMultipleLocations: boolean;
  source?: string;
  showCalendarV2: boolean;
  showGravityBasedApps: boolean;
  showAIReceptionist: boolean;
  translate: (key: string) => string;
}

export const getMenuItems = (config: MenuConfiguration): MenuItem[] => {
  return [
    ...getDashboardMenuItem(config),
    ...getCalendarMenuItem(config),
    ...getCalendarV2MenuItem(config),
    ...getPOSManagerMenuItem(config),
    ...getBookingPageMenuItem(config),
    ...getShiftPlanMenuItem(config),
    ...getAIReceptionistMenuItem(config),
    ...getManagementMenuItems(config),
    ...getMarketingMenuItems(config),
    ...getAnalysisMenuItems(config),
    ...getSetupMenuItems(config),
  ].filter(Boolean);
};
